import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Chemical Template Item
 */
export interface ChemicalTemplateItem {
  id?: string;
  templateId?: string;
  chemicalId?: string | null;
  customChemicalId?: string | null;
  name: string;
  epaNumber?: string;
  source?: 'epa' | 'adn-recommended' | 'custom' | null;
  rate: string;
  rateValue?: number | null;
  rateUnit?: string | null;
  ratePerUnit?: string | null;
  purpose?: string;
  sortOrder?: number;
}

/**
 * Chemical Template (with items)
 */
export interface ChemicalTemplate {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  targetCrops?: string[];
  targetPests?: string[];
  applicationTiming?: string;
  totalVolume?: string;
  estimatedCostPerAcre?: number;
  notes?: string;
  usageCount: number;
  items?: ChemicalTemplateItem[];
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * DTO for creating chemical template
 */
export interface CreateChemicalTemplateDto {
  name: string;
  description?: string;
  targetCrops?: string[];
  targetPests?: string[];
  applicationTiming?: string;
  totalVolume?: string;
  estimatedCostPerAcre?: number;
  notes?: string;
  chemicals: {
    chemicalId?: string;
    customChemicalId?: string;
    name: string;
    epaNumber?: string;
    rate: string;
    rateValue?: number | null;
    rateUnit?: string | null;
    ratePerUnit?: string | null;
    purpose?: string;
  }[];
}

/**
 * DTO for updating chemical template
 */
export interface UpdateChemicalTemplateDto extends Partial<CreateChemicalTemplateDto> {}

/**
 * Formatted chemicals for spray record form
 */
export interface TemplateChemicalCopy {
  chemicalId?: string;
  customChemicalId?: string;
  name: string;
  epaNumber?: string;
  rate: string;
  rateValue?: number | null;
  rateUnit?: string | null;
  ratePerUnit?: string | null;
  source: 'template';
  templateName: string;
  purpose?: string;
}

/**
 * API Response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Chemical Template Service
 *
 * Manages reusable chemical mix templates for spray records.
 * Requires X-Organization-Id header (handled by AuthInterceptor).
 *
 * Reference: /agent-os/specs/2026-01-07-chemical-templates-backend/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class ChemicalTemplateService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // Signal-based state management
  private templatesSignal = signal<ChemicalTemplate[]>([]);
  private loadingSignal = signal<boolean>(false);
  private errorSignal = signal<string | null>(null);

  // Public computed signals
  readonly templates = computed(() => this.templatesSignal());
  readonly isLoading = computed(() => this.loadingSignal());
  readonly error = computed(() => this.errorSignal());

  // Sorted by usage count (most used first)
  readonly popularTemplates = computed(() =>
    [...this.templatesSignal()].sort((a, b) => b.usageCount - a.usageCount)
  );

  /**
   * Load chemical templates for current organization
   * GET /chemical-templates?search=
   */
  async loadTemplates(options: {
    search?: string;
  } = {}): Promise<void> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const params: any = {};
      if (options.search) params.search = options.search;

      const response = await firstValueFrom(
        this.http.get<ApiResponse<ChemicalTemplate[]>>(
          `${this.apiUrl}/chemical-templates`,
          { params }
        )
      );

      if (response.success && response.data) {
        this.templatesSignal.set(response.data);
      } else {
        throw new Error(response.error || 'Failed to load chemical templates');
      }
    } catch (error: any) {
      console.error('Error loading chemical templates:', error);
      this.errorSignal.set(error.message || 'Failed to load chemical templates');
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Get chemical template by ID (with all items)
   * GET /chemical-templates/:id
   */
  async getTemplateById(id: string): Promise<ChemicalTemplate | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<ApiResponse<ChemicalTemplate>>(
          `${this.apiUrl}/chemical-templates/${id}`
        )
      );

      if (response.success && response.data) {
        return response.data;
      }

      return null;
    } catch (error: any) {
      console.error('Error fetching chemical template:', error);
      this.errorSignal.set(error.message || 'Failed to fetch chemical template');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Create a new chemical template with items
   * POST /chemical-templates
   */
  async createTemplate(dto: CreateChemicalTemplateDto): Promise<ChemicalTemplate | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<ChemicalTemplate>>(
          `${this.apiUrl}/chemical-templates`,
          dto
        )
      );

      if (response.success && response.data) {
        // Add to local state
        this.templatesSignal.update(templates => [...templates, response.data!]);
        return response.data;
      }

      throw new Error(response.error || 'Failed to create chemical template');
    } catch (error: any) {
      console.error('Error creating chemical template:', error);
      this.errorSignal.set(error.message || 'Failed to create chemical template');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Update an existing chemical template with items
   * PUT /chemical-templates/:id
   */
  async updateTemplate(
    id: string,
    dto: UpdateChemicalTemplateDto
  ): Promise<ChemicalTemplate | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.put<ApiResponse<ChemicalTemplate>>(
          `${this.apiUrl}/chemical-templates/${id}`,
          dto
        )
      );

      if (response.success && response.data) {
        // Update local state
        this.templatesSignal.update(templates => {
          const index = templates.findIndex(t => t.id === id);
          if (index !== -1) {
            const updated = [...templates];
            updated[index] = response.data!;
            return updated;
          }
          return templates;
        });

        return response.data;
      }

      throw new Error(response.error || 'Failed to update chemical template');
    } catch (error: any) {
      console.error('Error updating chemical template:', error);
      this.errorSignal.set(error.message || 'Failed to update chemical template');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Delete a chemical template (soft-delete)
   * DELETE /chemical-templates/:id
   */
  async deleteTemplate(id: string): Promise<boolean> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.delete<ApiResponse<void>>(
          `${this.apiUrl}/chemical-templates/${id}`
        )
      );

      if (response.success) {
        // Remove from local state
        this.templatesSignal.update(templates => templates.filter(t => t.id !== id));
        return true;
      }

      throw new Error(response.error || 'Failed to delete chemical template');
    } catch (error: any) {
      console.error('Error deleting chemical template:', error);
      this.errorSignal.set(error.message || 'Failed to delete chemical template');
      return false;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Copy template to spray record (formats chemicals and increments usage count)
   * POST /chemical-templates/:id/copy
   */
  async copyToSprayRecord(id: string): Promise<TemplateChemicalCopy[] | null> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<TemplateChemicalCopy[]>>(
          `${this.apiUrl}/chemical-templates/${id}/copy`,
          {}
        )
      );

      if (response.success && response.data) {
        // Update local usage count
        this.templatesSignal.update(templates => {
          const index = templates.findIndex(t => t.id === id);
          if (index !== -1) {
            const updated = [...templates];
            updated[index] = {
              ...updated[index],
              usageCount: updated[index].usageCount + 1
            };
            return updated;
          }
          return templates;
        });

        return response.data;
      }

      throw new Error(response.error || 'Failed to copy template');
    } catch (error: any) {
      console.error('Error copying template:', error);
      this.errorSignal.set(error.message || 'Failed to copy template');
      return null;
    } finally {
      this.loadingSignal.set(false);
    }
  }

  /**
   * Search templates (client-side filtering)
   */
  searchTemplates(query: string): ChemicalTemplate[] {
    if (!query || query.trim().length < 2) {
      return this.templatesSignal();
    }

    const searchTerm = query.toLowerCase().trim();
    return this.templatesSignal().filter(t =>
      t.name.toLowerCase().includes(searchTerm) ||
      t.description?.toLowerCase().includes(searchTerm) ||
      t.targetCrops?.some(c => c.toLowerCase().includes(searchTerm)) ||
      t.targetPests?.some(p => p.toLowerCase().includes(searchTerm))
    );
  }

  /**
   * Get templates by target crop (client-side filtering)
   */
  getTemplatesByCrop(crop: string): ChemicalTemplate[] {
    return this.templatesSignal().filter(t =>
      t.targetCrops?.some(c => c.toLowerCase() === crop.toLowerCase())
    );
  }

  /**
   * Get templates by target pest (client-side filtering)
   */
  getTemplatesByPest(pest: string): ChemicalTemplate[] {
    return this.templatesSignal().filter(t =>
      t.targetPests?.some(p => p.toLowerCase() === pest.toLowerCase())
    );
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.errorSignal.set(null);
  }
}
